import React from "react";
import { useNavigationPaths } from "newui/features/Auth/hooks/useNavigationPaths";
import type { RfxViewMode } from "../../hooks/useRfxViewMode";
import type { RfxListPageMeta } from "../../hooks/useRfxList";
import type { RfxListItemResource } from "../../types/rfx-list.type";
import { getPanelRowTarget } from "./panelRowTarget";
import RFQGridView from "../legacy/RFQGridView";
import RFQListView from "../legacy/RFQListView";
import { toLegacyRfxRow } from "../legacy/toLegacyRfxRow";

export interface PanelListProps {
	meta: RfxListPageMeta | undefined;
	rows: RfxListItemResource[];
	/** Grid/list mode, owned by `RfxListPage`'s toolbar (legacy parity). */
	view: RfxViewMode;
}

/**
 * Panel row rendering — plugs into 007's `RfxListPage` `renderList` slot.
 * Server-authoritative: rows render exactly what `useRfxList` returned for
 * the active tab (no client-side re-filtering). Click-through target is
 * decided by `getPanelRowTarget` from the row's own `rfx_evaluation_id`
 * (the only per-row signal on the wire — see panelRowTarget.ts).
 *
 * TASK-027 (second pass, team-lead ruling): shares the SAME restored legacy
 * `RFQGridView`/`RFQListView`/`RFQCard` (`components/legacy/`) T&Q uses,
 * instead of the TASK-023 lookalike `PanelCard`/inline table (now deleted).
 * Widening `RfxListItemResource` with T&Q-only fields (supplier counts,
 * avatars, rfxType, unread messages) would leak procurer-side data to
 * cross-company panel members — the leak class TASK-021 closed — so the row
 * is adapted via `toLegacyRfxRow` (maps only the fields Panel actually has;
 * T&Q-only fields are simply omitted, not faked) and the legacy components
 * render those fields conditionally when absent — see RFQCard.js's header
 * comment for the exact gates. `linkOverride` preserves `getPanelRowTarget`'s
 * evaluation-deep-link-vs-preview click-through, which the legacy
 * `clientPaths.viewRfx`/`editRfx` owner routing cannot express.
 */
export const PanelList = ({ rows, view }: PanelListProps) => {
	const { clientPaths } = useNavigationPaths();

	const legacyRows = rows.map((row) =>
		toLegacyRfxRow(row, clientPaths ? getPanelRowTarget(clientPaths, row) : "#")
	);

	return (
		<div data-testid="panel-list">
			{view === "grid" ? (
				<div data-testid="panel-grid">
					<RFQGridView
						rows={legacyRows}
						isLoading={false}
						onExtendClick={() => undefined}
						isSidebarCollapsed={false}
					/>
				</div>
			) : (
				<div data-testid="panel-table">
					<RFQListView rows={legacyRows} isLoading={false} onExtendClick={() => undefined} />
				</div>
			)}
		</div>
	);
};
